import { useCallback, useEffect, useRef, useState } from "react";
import { preventivoApi } from "@/api/preventivo";
import { ApiError } from "@/api/client";
import { leggiCoda, salvaCoda } from "@/lib/pagineScattate";

/** Una bolletta finita che aspetta di arrivare al server. */
export interface VoceCoda {
  id: string;
  pagine: Blob[];
  fallito: boolean;
  /** Il preventivo dove la bolletta è stata fatta: parte lì, anche se nel frattempo la pagina ne mostra un altro. */
  pid: string;
  /** La coda salvata sul telefono dove sta: una per preventivo. */
  chiave: string;
  /** Il server ha detto no per sempre (4xx): ripetere non serve, resta solo «Elimina». */
  rifiutato?: boolean;
  /** Il rifiuto è «troppe_bollette» (409): la voce dice che il massimo è raggiunto, non «Invio non riuscito». */
  tetto?: boolean;
  /** «Rifai la foto»: la bolletta vecchia, che si toglie solo dopo che questa è arrivata. */
  sostituisce?: string;
}

/** Rifiuto definitivo: un 4xx che non sia «riprova più tardi» (408, 429) né la sessione scaduta (401). */
function rifiutoDefinitivo(e: unknown): boolean {
  return e instanceof ApiError && e.status >= 400 && e.status < 500 && ![401, 408, 429].includes(e.status);
}

/** Il server ha già il massimo di bollette: 409 «troppe_bollette», un rifiuto definitivo come gli altri 4xx. */
export function troppeBollette(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409 && e.detail === "troppe_bollette";
}

function nuovoId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** La coda d'invio delle bollette fotografate: una per volta, nell'ordine; salvata sul telefono finché il server non
 *  ha risposto (una scheda chiusa da Android non la perde). Se una non parte si ferma lì, con «Riprova»: quelle dietro
 *  aspettano. `onInviata` rilegge la vista; la voce se ne va solo dopo, così la bolletta c'è sempre, una volta sola.
 *  Il telefono dà a ogni bolletta un identificativo, lo stesso a ogni tentativo: il server non ne crea due.
 *
 *  Ogni voce porta il suo preventivo (`pid`) e la sua coda salvata (`chiave`): passando da un preventivo all'altro le
 *  voci in volo restano dove sono nate e partono lì; `voci` è solo quelle del preventivo che la pagina mostra. */
export function useCodaFoto(chiave: string, pid: string, onInviata: () => Promise<void> | void) {
  const [voci, setVoci] = useState<VoceCoda[]>([]);
  const ref = useRef<VoceCoda[]>([]);        // le voci di tutti i preventivi, nell'ordine di partenza
  const chiavi = useRef(new Set<string>());  // le code salvate toccate: quella che si svuota va svuotata anche su disco
  const inVolo = useRef(false);
  const dopo = useRef(onInviata);
  dopo.current = onInviata;
  const pidRef = useRef(pid);
  pidRef.current = pid;

  const metti = useCallback((v: VoceCoda[]) => {
    ref.current = v;
    setVoci(v.filter((x) => x.pid === pidRef.current));
    for (const x of v) chiavi.current.add(x.chiave);
    return Promise.all([...chiavi.current].map((k) => salvaCoda(
      k, v.filter((x) => x.chiave === k).map(({ id, pagine, sostituisce }) => ({ id, pagine, sostituisce })),
    ))).then(() => undefined);
  }, []);

  const parti = useCallback(async () => {
    if (inVolo.current) return;
    inVolo.current = true;
    try {
      for (;;) {
        // la prima che può partire: una voce ferma (fallita) blocca quelle dietro del suo preventivo, non degli altri
        const ferme = new Set<string>();
        let testa: VoceCoda | undefined;
        for (const v of ref.current) {
          if (v.fallito) ferme.add(v.pid);
          else if (!ferme.has(v.pid)) { testa = v; break; }
        }
        if (!testa) break;
        try {
          await preventivoApi.caricaFoto(testa.pid, testa.pagine, testa.id, testa.sostituisce);
          // la nuova c'è: ora la vecchia se ne va (già sparita = va bene). Se non riesce, la voce resta con «Riprova»:
          // stesso identificativo, il server non crea la nuova due volte.
          if (testa.sostituisce) {
            await preventivoApi.elimina(testa.pid, testa.sostituisce).catch((e: unknown) => {
              if (!(e instanceof ApiError && e.status === 404)) throw e;
            });
          }
        } catch (e) {
          const tetto = troppeBollette(e);
          const rifiutato = tetto || rifiutoDefinitivo(e);
          void metti(ref.current.map((v) => (v.id === testa.id ? { ...v, fallito: true, rifiutato, tetto } : v)));
          break;
        }
        await dopo.current();
        void metti(ref.current.filter((v) => v.id !== testa.id));
      }
    } finally {
      inVolo.current = false;
    }
  }, [metti]);

  // Al ritorno nella pagina (anche a scheda scartata) quel che era in coda riparte da sé; cambiando preventivo si
  // mostrano le voci del nuovo e si rilegge la sua coda salvata (senza raddoppiare quelle già in memoria).
  useEffect(() => {
    let viva = true;
    setVoci(ref.current.filter((x) => x.pid === pid));
    void leggiCoda(chiave).then((salvate) => {
      if (!viva) return;
      // la chiave si annota solo a coda salvata unita: se il preventivo cambia durante la lettura, il salvataggio di
      // un'altra voce non deve riscrivere questa coda con [] prima di averla letta
      chiavi.current.add(chiave);
      const presenti = new Set(ref.current.map((v) => v.id));
      const nuove = salvate.filter((s) => !presenti.has(s.id)).map((s) => ({ ...s, pid, chiave, fallito: false }));
      if (nuove.length === 0) return;
      ref.current = [...nuove, ...ref.current];
      setVoci(ref.current.filter((x) => x.pid === pidRef.current));
      void parti();
    });
    return () => { viva = false; };
  }, [chiave, pid, parti]);

  /** «Bolletta finita»: in coda, salvata sul telefono, e parte (se non c'è già un invio in corso). */
  const aggiungi = useCallback((pagine: Blob[], sostituisce?: string) => {
    const salvata = metti([...ref.current, { id: nuovoId(), pagine, fallito: false, sostituisce, pid: pidRef.current, chiave }]);
    void parti();
    return salvata;
  }, [metti, parti, chiave]);

  const riprova = useCallback((id: string) => {
    void metti(ref.current.map((v) => (v.id === id ? { ...v, fallito: false, rifiutato: false, tetto: false } : v)));
    void parti();
  }, [metti, parti]);

  /** «Elimina»: la bolletta non si manda più; via dalla coda e dal telefono, e quelle dietro ripartono. */
  const elimina = useCallback((id: string) => {
    void metti(ref.current.filter((v) => v.id !== id));
    void parti();
  }, [metti, parti]);

  /** Preventivo eliminato: le sue voci non partono più e le foto se ne vanno dal telefono (la sua coda salvata si svuota). */
  const scarta = useCallback((pidEliminato: string, chiaveSalvata: string) => {
    void metti(ref.current.filter((v) => v.pid !== pidEliminato));
    void salvaCoda(chiaveSalvata, []);
  }, [metti]);

  return { voci, aggiungi, riprova, elimina, scarta };
}
